import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2, Edit, Search, FileText, Receipt } from 'lucide-react';
import { adminApi } from '../services/adminApi';
import { Modal } from '../components/Modal';
import { Pagination } from '../components/Pagination';
import { ReceiptTemplatesPanel } from '../components/ReceiptTemplatesPanel';
import { COLORS } from '../../theme/colors';

const LIMIT = 30;

const NO_TERMS = {
  translation_usd_per_1k: '',
  recognition_usd_per_1k: '',
  scan_min_chars_per_page: '',
  monthly_limit_usd: '',
};

const EMPTY_FORM = {
  name: '',
  plan: 'limited',
  domain: '',
  postpaid: false,
  terms: NO_TERMS,
  invitedEmails: '',
};

const TERM_FIELDS = [
  { key: 'translation_usd_per_1k', label: 'Translation, $ per 1000 chars', step: '0.01' },
  { key: 'recognition_usd_per_1k', label: 'Recognition only, $ per 1000 chars', step: '0.01' },
  { key: 'scan_min_chars_per_page', label: 'Scan minimum, chars per page', step: '1' },
  { key: 'monthly_limit_usd', label: 'Monthly limit, $', step: '1' },
];

const termsText = (terms) => (
  `$${terms.translation_usd_per_1k.toFixed(2)} / $${terms.recognition_usd_per_1k.toFixed(2)} per 1000 · `
  + `min ${terms.scan_min_chars_per_page} · limit $${terms.monthly_limit_usd}`
);

export function WorkspacesPage() {
  const [workspaces, setWorkspaces] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingWorkspace, setEditingWorkspace] = useState(null);
  const [templatesPanelFor, setTemplatesPanelFor] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const loadWorkspaces = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.getWorkspaces({
        search: search || undefined,
        skip: (page - 1) * LIMIT,
        limit: LIMIT
      });
      setWorkspaces(data.items);
      setTotal(data.total);
    } catch (error) {
      console.error('Failed to load workspaces:', error);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    loadWorkspaces();
  }, [loadWorkspaces]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadWorkspaces();
  };

  const openCreateModal = () => {
    setEditingWorkspace(null);
    setFormData(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEditModal = (workspace) => {
    setEditingWorkspace(workspace);
    setFormData({
      name: workspace.name,
      plan: workspace.plan || 'limited',
      domain: workspace.domain || '',
      postpaid: !!workspace.postpaid,
      terms: workspace.postpaid
        ? Object.fromEntries(TERM_FIELDS.map(({ key }) => [key, String(workspace.postpaid[key])]))
        : NO_TERMS,
      invitedEmails: workspace.invited_emails.join('\n'),
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        name: formData.name,
        plan: formData.plan,
        domain: formData.domain || null,
      };

      // Terms and invites are set on an existing workspace, so a new one is created first, then updated.
      const saved = editingWorkspace || await adminApi.createWorkspace(data);
      await adminApi.updateWorkspace(saved.id, {
        ...data,
        postpaid: formData.plan === 'limited' && formData.postpaid
          ? Object.fromEntries(TERM_FIELDS.map(({ key }) => [key, Number(formData.terms[key])]))
          : null,
        invited_emails: formData.invitedEmails.split(/[\s,;]+/).filter(Boolean),
      });
      setModalOpen(false);
      loadWorkspaces();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save workspace');
    }
  };

  const handleDelete = async (workspace) => {
    if (!window.confirm(`Delete workspace "${workspace.name}"? This cannot be undone.`)) return;
    try {
      await adminApi.deleteWorkspace(workspace.id);
      loadWorkspaces();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete workspace');
    }
  };

  const getPlanBadge = (plan) => {
    if (plan === 'unlimited') return { text: 'Unlimited', className: 'admin-badge-success' };
    return { text: 'Limited', className: 'admin-badge-warning' };
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Workspaces</h1>
        <button className="admin-btn admin-btn-primary" onClick={openCreateModal}>
          <Plus size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          Create Workspace
        </button>
      </div>

      <form onSubmit={handleSearch} style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={18} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-disabled)' }} />
            <input
              type="text"
              className="admin-input"
              placeholder="Search by name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ paddingLeft: 40 }}
            />
          </div>
          <button type="submit" className="admin-btn admin-btn-secondary">Search</button>
        </div>
      </form>

      <div className="admin-card" style={{ padding: 0 }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Plan</th>
              <th>Domain</th>
              <th>Billing</th>
              <th>Created</th>
              <th style={{ width: 160 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: 40 }}>Loading...</td>
              </tr>
            ) : workspaces.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No workspaces found</td>
              </tr>
            ) : (
              workspaces.map(ws => {
                const planBadge = getPlanBadge(ws.plan);
                return (
                  <tr key={ws.id}>
                    <td><strong>{ws.name}</strong></td>
                    <td>
                      <span className={`admin-badge ${planBadge.className}`}>{planBadge.text}</span>
                    </td>
                    <td style={{ fontSize: 13, color: 'var(--text-muted)' }}>{ws.domain || '-'}</td>
                    <td style={{ fontSize: 13 }}>
                      {ws.postpaid ? (
                        <>
                          <span className="admin-badge admin-badge-info">Postpaid</span>
                          <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>{termsText(ws.postpaid)}</div>
                        </>
                      ) : (
                        <span style={{ color: 'var(--text-muted)' }}>Prepaid</span>
                      )}
                      {ws.invited_emails.length > 0 && (
                        <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>Invited: {ws.invited_emails.join(', ')}</div>
                      )}
                    </td>
                    <td>{new Date(ws.created_at).toLocaleDateString()}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {ws.postpaid && (
                          <Link
                            to={`/admin/workspaces/${ws.id}/report`}
                            style={{ padding: 4, display: 'inline-flex' }}
                            title="Month report"
                          >
                            <Receipt size={16} color={COLORS.textMuted} />
                          </Link>
                        )}
                        <button
                          onClick={() => setTemplatesPanelFor(ws)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                          title="Receipt templates"
                        >
                          <FileText size={16} color={COLORS.textMuted} />
                        </button>
                        <button
                          onClick={() => openEditModal(ws)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                          title="Edit"
                        >
                          <Edit size={16} color={COLORS.textMuted} />
                        </button>
                        <button
                          onClick={() => handleDelete(ws)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                          title="Delete"
                        >
                          <Trash2 size={16} color={COLORS.danger} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editingWorkspace ? 'Edit Workspace' : 'Create Workspace'}>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label className="admin-label">Name</label>
            <input
              type="text"
              className="admin-input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label className="admin-label">Plan</label>
            <select
              className="admin-input"
              value={formData.plan}
              onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
            >
              <option value="limited">Limited</option>
              <option value="unlimited">Unlimited</option>
            </select>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label className="admin-label">Domain (for corporate registration, e.g. company.com)</label>
            <input
              type="text"
              className="admin-input"
              value={formData.domain}
              onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
              placeholder="company.com"
            />
          </div>

          {formData.plan === 'limited' && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={formData.postpaid}
                  onChange={(e) => setFormData({ ...formData, postpaid: e.target.checked })}
                  data-testid="workspace-postpaid"
                />
                Postpaid: invoiced monthly at its own rates
              </label>
              {formData.postpaid && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                  {TERM_FIELDS.map(({ key, label, step }) => (
                    <div key={key}>
                      <label className="admin-label">{label}</label>
                      <input
                        type="number"
                        min="0"
                        step={step}
                        className="admin-input"
                        value={formData.terms[key]}
                        onChange={(e) => setFormData({ ...formData, terms: { ...formData.terms, [key]: e.target.value } })}
                        required
                        data-testid={`term-${key}`}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label className="admin-label">Invited emails (sign up straight into this workspace, one per line)</label>
            <textarea
              className="admin-input"
              rows={3}
              value={formData.invitedEmails}
              onChange={(e) => setFormData({ ...formData, invitedEmails: e.target.value })}
              placeholder="translator@gmail.com"
              data-testid="workspace-invites"
            />
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </button>
            <button type="submit" className="admin-btn admin-btn-primary">
              {editingWorkspace ? 'Save' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>

      <ReceiptTemplatesPanel
        workspace={templatesPanelFor}
        isOpen={templatesPanelFor !== null}
        onClose={() => setTemplatesPanelFor(null)}
      />
    </div>
  );
}
